import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { ClientContextErrorCodes, type BrandBrainReadModel, type BrandBrainSectionId } from "@nexus/contracts";
import { ApiError } from "@/lib/api-client";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { brandBrainAvailabilityQueryOptions, brandBrainQueryOptions } from "@/lib/queries";
import {
  brandBrainEditableValues,
  brandBrainRows,
  brandBrainSections,
  brandBrainSummary,
} from "@/features/brand-brain/lib/brand-brain-view";
import { brandBrainSectionLabel } from "@/features/brand-brain/lib/brand-brain-labels";
import { useUpdateBrandBrain } from "@/features/brand-brain/hooks/useUpdateBrandBrain";
import { registerTenantSwitchGuard, type TenantSwitchRequest } from "@/features/auth/stores/tenant-switch-guard.store";
import { BrandBrainJobError } from "@/features/brand-brain/api/brand-brain.api";
import { OrgDrawerUnsavedChangesGuard } from "@/features/identity-hub/components/dashboard/shared/drawer/OrgDrawerUnsavedChangesGuard";
import {
  brandBrainDraftFromValues,
  changedBrandBrainColumns,
  parseBrandBrainDraft,
  type BrandBrainDraft,
  type BrandBrainFieldErrors,
} from "@/features/brand-brain/lib/brand-brain-draft";
import {
  onboardingSaveErrorBanner,
  toOnboardingSaveError,
  type OnboardingSaveError,
} from "@/features/identity-hub/lib/onboarding-save-error";
import {
  BrandBrainSectionCard,
  INTERACTIVE_TARGET_CLASS,
  MONO_LABEL,
  NOT_COLLECTED_CHIP,
  type BrandBrainSectionEditor,
} from "./BrandBrainSectionCard";

type FieldFilter = "all" | "gaps";

/** Resolves to the reason when the save was rejected; the view keeps the draft for a retry. */
export type BrandBrainSaveHandler = (changes: Record<string, unknown>) => Promise<OnboardingSaveError | null>;

/** The unsaved-changes guard, opened by the page when a navigation is blocked. */
export interface BrandBrainLeaveGuard {
  isOpen: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

interface EditSession {
  tenantId: string;
  customerName: string;
  sectionId: BrandBrainSectionId;
}

/** A section (and optionally one field) the user asked to open while another section has unsaved changes. */
interface PendingEdit {
  sectionId: BrandBrainSectionId;
  column?: string;
}

const NO_FIELD_ERRORS: BrandBrainFieldErrors = {};
const GUARD_SCOPE = "brandBrain.unsavedGuard";

export function BrandBrainPage() {
  const { t } = useTranslation();
  const tenantId = useAuth((state) => state.activeTenantId) ?? "";
  const query = useQuery(brandBrainQueryOptions(tenantId));
  const availabilityQuery = useQuery(brandBrainAvailabilityQueryOptions());
  const hasUpdatePermission = usePermissions().BRAND_BRAIN_UPDATE.isOn;
  const isInternal = useIsSuperTenant();
  const canEdit = hasUpdatePermission && availabilityQuery.data?.writesEnabled === true;
  const isEditingTurnedOff = hasUpdatePermission && availabilityQuery.data?.writesEnabled === false;
  const updateMut = useUpdateBrandBrain();
  const [isDirty, setIsDirty] = useState(false);
  // Read through a ref so a discard is seen at once: a task's navigation runs in a microtask, before the next render.
  const isDirtyRef = useRef(false);
  const blocker = useBlocker({
    shouldBlockFn: () => isDirtyRef.current,
    enableBeforeUnload: () => isDirtyRef.current,
    withResolver: true,
  });
  /** The organization switch held back while the user decides about unsaved changes. */
  const [pendingTenantSwitch, setPendingTenantSwitch] = useState<TenantSwitchRequest | null>(null);
  // The guard callback outlives renders, so it reads the held request from a ref, never from stale state.
  const pendingTenantSwitchRef = useRef<TenantSwitchRequest | null>(null);

  const holdTenantSwitch = (request: TenantSwitchRequest | null) => {
    pendingTenantSwitchRef.current = request;
    setPendingTenantSwitch(request);
  };

  const handleDirtyChange = (next: boolean) => {
    isDirtyRef.current = next;
    setIsDirty(next);
  };

  // Switching organization changes no route, so the blocker above never sees it; every switch asks this guard instead.
  useEffect(() => {
    if (!isDirty) return;
    return registerTenantSwitchGuard((request) => {
      // The latest switch wins; settling the earlier one keeps whatever awaits it (a Retry) from hanging.
      pendingTenantSwitchRef.current?.cancel();
      holdTenantSwitch(request);
      return true;
    });
  }, [isDirty]);

  // Leaving the page with a switch still held must settle it too.
  useEffect(() => () => pendingTenantSwitchRef.current?.cancel(), []);

  const handleSave: BrandBrainSaveHandler = async (changes) => {
    if ((useAuth.getState().activeTenantId ?? "") !== tenantId) {
      return { message: t("brandBrain.edit.saveFailed", "Brand Brain was not saved. Try again.") };
    }
    try {
      await updateMut.mutateAsync(changes);
      return null;
    } catch (err) {
      return toOnboardingSaveError(err, t("brandBrain.edit.saveFailed", "Brand Brain was not saved. Try again."), t);
    }
  };

  return (
    <BrandBrainView
      model={query.data}
      isLoading={query.isLoading}
      isError={query.isError}
      isNotConfigured={isCustomerNotConfigured(query.error)}
      onRetry={() => void query.refetch()}
      tenantId={tenantId}
      canEdit={canEdit}
      canEditNexusFields={isInternal}
      isEditingTurnedOff={isEditingTurnedOff}
      isSaving={updateMut.isPending}
      onSave={handleSave}
      onDirtyChange={handleDirtyChange}
      leaveGuard={{
        isOpen: blocker.status === "blocked" || pendingTenantSwitch !== null,
        onKeepEditing: () => {
          pendingTenantSwitchRef.current?.cancel();
          holdTenantSwitch(null);
          blocker.reset?.();
        },
        onDiscard: () => {
          isDirtyRef.current = false;
          const held = pendingTenantSwitchRef.current;
          if (held) {
            holdTenantSwitch(null);
            held.proceed();
            return;
          }
          blocker.proceed?.();
        },
      }}
    />
  );
}

/** The organization has no customer partition, so it has no Brand Brain customer to read. */
function isCustomerNotConfigured(error: Error | null): boolean {
  const code = error instanceof ApiError || error instanceof BrandBrainJobError ? error.code : undefined;
  return code === ClientContextErrorCodes.CUSTOMER_NOT_CONFIGURED;
}

export function BrandBrainView({
  model,
  isLoading,
  isError,
  isNotConfigured = false,
  onRetry,
  tenantId = "",
  canEdit = false,
  canEditNexusFields = false,
  isEditingTurnedOff = false,
  isSaving = false,
  onSave,
  onDirtyChange,
  leaveGuard,
}: {
  model?: BrandBrainReadModel;
  isLoading: boolean;
  isError: boolean;
  /** No customer partition: shown as not set up, never as a retryable failure. */
  isNotConfigured?: boolean;
  onRetry: () => void;
  /** Active tenant. An edit started for another tenant cannot be saved here. */
  tenantId?: string;
  /** BRAND_BRAIN_UPDATE on the active tenant. */
  canEdit?: boolean;
  /** An Adge team member: may also edit the Nexus-owned fields (approved claims, what works, production rules). */
  canEditNexusFields?: boolean;
  /** The caller may edit, but this deployment does not accept onboarding writes yet. */
  isEditingTurnedOff?: boolean;
  isSaving?: boolean;
  onSave?: BrandBrainSaveHandler;
  /** Reports whether the open section has unsaved changes, so the page can guard navigation. */
  onDirtyChange?: (isDirty: boolean) => void;
  leaveGuard?: BrandBrainLeaveGuard;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FieldFilter>("all");
  const [draft, setDraft] = useState<BrandBrainDraft | null>(null);
  const [initialDraft, setInitialDraft] = useState<BrandBrainDraft>({});
  const [saveError, setSaveError] = useState<OnboardingSaveError | null>(null);
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);
  /** The field to focus once its input is rendered; the counter re-runs the focus for the same field. */
  const [focusRequest, setFocusRequest] = useState<{ column: string; seq: number } | null>(null);
  const editSession = useRef<EditSession | null>(null);
  const customerName = model?.customerName ?? "";

  useEffect(() => {
    editSession.current = null;
    setDraft(null);
    setSaveError(null);
    setPendingEdit(null);
  }, [tenantId, customerName]);

  const rows = useMemo(
    () => (model ? brandBrainRows(model, { canEditNexus: canEditNexusFields }) : []),
    [model, canEditNexusFields],
  );
  const editingSectionId = draft ? (editSession.current?.sectionId ?? null) : null;
  const sectionEditableColumns = useMemo(
    () =>
      editingSectionId
        ? rows.filter((row) => row.editable && row.section === editingSectionId).map((row) => row.column)
        : [],
    [rows, editingSectionId],
  );
  const changedColumns = useMemo(
    () =>
      draft ? changedBrandBrainColumns(draft, initialDraft).filter((column) => sectionEditableColumns.includes(column)) : [],
    [draft, initialDraft, sectionEditableColumns],
  );
  const parsedDraft = useMemo(() => (draft ? parseBrandBrainDraft(draft, changedColumns) : null), [draft, changedColumns]);
  const isDirty = changedColumns.length > 0;
  const hasDraftErrors = Object.keys(parsedDraft?.errors ?? NO_FIELD_ERRORS).length > 0;
  const canStartEdit = canEdit && Boolean(onSave) && Boolean(model?.clientContext.exists);
  const sessionMatches =
    editSession.current?.tenantId === tenantId && editSession.current.customerName === customerName;
  const canSave = canEdit && sessionMatches && isDirty && !hasDraftErrors && !isSaving;

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!focusRequest) return;
    const field = document.querySelector<HTMLElement>(`[data-testid="brand-brain-field-${focusRequest.column}"]`);
    field?.scrollIntoView?.({ block: "center" });
    field?.querySelector<HTMLElement>("input, textarea")?.focus();
  }, [focusRequest]);

  const requestFocus = (column: string) => setFocusRequest((current) => ({ column, seq: (current?.seq ?? 0) + 1 }));

  /** Seeds the draft with only this section's editable fields, so a save can never carry another section's values. */
  const startEdit = (sectionId: BrandBrainSectionId, column?: string) => {
    if (!model || !canStartEdit) return;
    const columns = new Set(rows.filter((row) => row.editable && row.section === sectionId).map((row) => row.column));
    const values = Object.fromEntries(Object.entries(brandBrainEditableValues(model)).filter(([key]) => columns.has(key)));
    const seeded = brandBrainDraftFromValues(values);
    editSession.current = { tenantId, customerName: model.customerName, sectionId };
    setDraft(seeded);
    setInitialDraft(seeded);
    setSaveError(null);
    setPendingEdit(null);
    const target = column ?? [...columns][0];
    if (target) requestFocus(target);
  };

  const handleEdit = (sectionId: BrandBrainSectionId, column?: string) => {
    if (editingSectionId === sectionId) {
      if (column) requestFocus(column);
      return;
    }
    if (isDirty) {
      setPendingEdit({ sectionId, column });
      return;
    }
    startEdit(sectionId, column);
  };

  const handleCancel = () => {
    editSession.current = null;
    setDraft(null);
    setSaveError(null);
  };

  const handleUndo = (column: string) => {
    setDraft((current) => (current ? { ...current, [column]: initialDraft[column as keyof BrandBrainDraft] } : current));
    if (saveError?.field === column) setSaveError(null);
  };

  const handleSave = async () => {
    const session = editSession.current;
    if (!canSave || !parsedDraft || !onSave || !session) return;
    if (session.tenantId !== tenantId || session.customerName !== customerName || !canEdit) return;
    setSaveError(null);
    const error = await onSave(parsedDraft.values);
    if (editSession.current !== session) return;
    if (error) {
      setSaveError(error);
      if (error.field && sectionEditableColumns.includes(error.field)) requestFocus(error.field);
      return;
    }
    editSession.current = null;
    setDraft(null);
  };

  const editor: BrandBrainSectionEditor | null = draft
    ? {
        draft,
        errors: parsedDraft?.errors ?? NO_FIELD_ERRORS,
        saveError,
        saveErrorBanner: onboardingSaveErrorBanner(saveError, sectionEditableColumns),
        changedColumns,
        canSave,
        isSaving,
        onChange: (column, value) => setDraft((current) => (current ? { ...current, [column]: value } : current)),
        onUndo: handleUndo,
        onSave: () => void handleSave(),
        onCancel: handleCancel,
      }
    : null;
  const summary = useMemo(() => brandBrainSummary(rows), [rows]);
  const sections = useMemo(() => {
    const filtered = brandBrainSections(rows, filter === "gaps");
    if (!editingSectionId) return filtered;
    // The section being edited keeps all its fields, even under Gaps only, so nothing disappears mid-edit.
    return brandBrainSections(rows, false).flatMap((section) => {
      if (section.id === editingSectionId) return [section];
      const match = filtered.find((candidate) => candidate.id === section.id);
      return match ? [match] : [];
    });
  }, [rows, filter, editingSectionId]);
  const [selectedSectionId, setSelectedSectionId] = useState<BrandBrainSectionId | null>(null);
  const activeSectionId = sections.some((section) => section.id === selectedSectionId)
    ? selectedSectionId
    : (sections[0]?.id ?? null);
  const hasRecord = Boolean(model) && !isLoading && !isError;
  const hasRows = hasRecord && rows.length > 0;
  const editingSectionLabel = editingSectionId ? brandBrainSectionLabel(t, editingSectionId) : "";
  const isGuardOpen = pendingEdit !== null || leaveGuard?.isOpen === true;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-background px-4 pt-6 pb-10 md:px-8">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-[18px]">
          <header className="flex flex-wrap items-start gap-5">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("brandBrain.title", "Brand Brain")}</h1>
              {model ? (
                <p className="mt-1 max-w-[68ch] text-[13px] text-muted-foreground">
                  {t("brandBrain.subtitle", {
                    name: model.customerName,
                    defaultValue: "Everything Adge knows about {{name}}, and where the record is still incomplete.",
                  })}
                </p>
              ) : null}
              {hasRows ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="flex gap-2" role="group" aria-label={t("brandBrain.filters.label", "Field filter")}>
                    <FilterChip
                      pressed={filter === "all"}
                      onClick={() => setFilter("all")}
                      label={t("brandBrain.filters.all", "All fields")}
                      pressedClassName="bg-slate-chip-bg text-slate-chip-text border-slate-chip-bg"
                      idleClassName="border-border text-muted-foreground hover:bg-slate-chip-bg hover:text-slate-chip-text"
                    />
                    <FilterChip
                      pressed={filter === "gaps"}
                      onClick={() => setFilter("gaps")}
                      label={t("brandBrain.filters.gapsWithCount", {
                        count: summary.notCollected,
                        defaultValue: "Gaps only · {{count}}",
                      })}
                      pressedClassName="border-warning bg-warning text-warning-foreground"
                      idleClassName="border-warning bg-warning-subtle text-warning"
                    />
                  </div>
                  <p className="ml-1.5 text-[11.5px] text-gray-400">
                    {canStartEdit
                      ? t("brandBrain.accessNoteEditable", "Internal roles only · onboarding fields are editable, generated fields are read-only")
                      : isEditingTurnedOff
                        ? t("brandBrain.accessNoteWritesOff", "Internal roles only · editing is turned off on this deployment")
                        : t("brandBrain.accessNote", "Internal roles only · read-only except production-kit files")}
                  </p>
                </div>
              ) : null}
            </div>
            {hasRecord ? <SummaryCounters summary={summary} /> : null}
          </header>

          {isLoading ? (
            <p className="text-sm text-muted-foreground" role="status">
              {t("brandBrain.loading", "Loading Brand Brain…")}
            </p>
          ) : null}

          {isError && isNotConfigured ? (
            <p
              className="rounded-lg border border-border-subtle bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
              data-testid="brand-brain-not-configured"
            >
              {t(
                "brandBrain.notConfigured",
                "Brand Brain is not set up for this organization. Set its Customer partition in Identity Hub first.",
              )}
            </p>
          ) : null}

          {isError && !isNotConfigured ? (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3" role="alert">
              <p className="text-sm text-foreground">{t("brandBrain.loadError", "Brand Brain could not be loaded.")}</p>
              <button
                type="button"
                className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                onClick={onRetry}
              >
                {t("brandBrain.retry", "Try again")}
              </button>
            </div>
          ) : null}

          {hasRecord && rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("brandBrain.empty", "No fields in this record.")}</p>
          ) : null}

          {hasRows ? (
            <>
              <RowLegend />

              <div className="flex flex-col items-start gap-6 lg:flex-row lg:gap-[26px]">
                <nav aria-labelledby="brand-brain-sections-title" className="w-full min-w-0 lg:sticky lg:top-0 lg:w-[220px] lg:flex-none">
                  <h2 id="brand-brain-sections-title" className={cn(MONO_LABEL, "px-2 pb-2")}>
                    {t("brandBrain.sectionsNav", "Sections")}
                  </h2>
                  <ul className="flex min-w-0 gap-1 overflow-x-auto lg:flex-col lg:gap-0 lg:overflow-visible">
                    {sections.map((section) => (
                      <li key={section.id} className="shrink-0 lg:shrink">
                        <button
                          type="button"
                          aria-current={section.id === activeSectionId ? "true" : undefined}
                          className={cn(
                            INTERACTIVE_TARGET_CLASS,
                            "flex w-full items-baseline gap-[9px] rounded-sm px-2 py-1.5 text-left text-xs text-secondary-subtle-foreground hover:bg-muted max-lg:whitespace-nowrap lg:whitespace-normal",
                            section.id === activeSectionId && "bg-muted font-semibold text-foreground",
                          )}
                          onClick={() => {
                            setSelectedSectionId(section.id);
                            document.getElementById(`brand-brain-${section.id}`)?.scrollIntoView({ block: "start" });
                          }}
                        >
                          <span className="w-3.5 flex-none font-mono text-[10px] text-gray-400">{section.number}</span>
                          <span className="min-w-0">{brandBrainSectionLabel(t, section.id)}</span>
                          <SectionStatus gapCount={section.gapCount} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="flex w-full min-w-0 flex-1 flex-col gap-4">
                  {filter === "gaps" && summary.notCollected === 0 ? (
                    <p className="text-sm text-muted-foreground">{t("brandBrain.noGaps", "No gaps in this record.")}</p>
                  ) : null}
                  {sections.map((section) => (
                    <BrandBrainSectionCard
                      key={section.id}
                      section={section}
                      editor={section.id === editingSectionId ? editor : null}
                      canEdit={canStartEdit}
                      onEdit={(column) => handleEdit(section.id, column)}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <OrgDrawerUnsavedChangesGuard
        open={isGuardOpen}
        translationScope={GUARD_SCOPE}
        title={t(`${GUARD_SCOPE}.title`, "Discard unsaved changes?")}
        body={t(`${GUARD_SCOPE}.body`, {
          section: editingSectionLabel,
          count: changedColumns.length,
          defaultValue: "You have unsaved changes in {{section}}. They will be lost if you continue.",
        })}
        onKeepEditing={() => {
          setPendingEdit(null);
          leaveGuard?.onKeepEditing();
        }}
        onDiscard={() => {
          if (pendingEdit) {
            startEdit(pendingEdit.sectionId, pendingEdit.column);
            return;
          }
          handleCancel();
          leaveGuard?.onDiscard();
        }}
      />
    </div>
  );
}

function FilterChip({
  pressed,
  onClick,
  label,
  pressedClassName,
  idleClassName,
}: {
  pressed: boolean;
  onClick: () => void;
  label: string;
  pressedClassName: string;
  idleClassName: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        INTERACTIVE_TARGET_CLASS,
        "inline-flex items-center rounded-full border px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors",
        pressed ? pressedClassName : idleClassName,
      )}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function SummaryCounters({ summary }: { summary: { total: number; collected: number; notCollected: number } }) {
  const { t } = useTranslation();
  return (
    <dl
      className="flex flex-none items-center gap-[22px] rounded-md border bg-muted px-[18px] py-3.5"
      aria-label={t("brandBrain.counters.label", "Record summary")}
    >
      <Counter value={summary.total} label={t("brandBrain.counters.fields", "Fields in the record")} />
      <span className="w-px self-stretch bg-border" aria-hidden="true" />
      <Counter value={summary.collected} label={t("brandBrain.counters.collected", "Collected")} />
      <span className="w-px self-stretch bg-border" aria-hidden="true" />
      <Counter
        value={summary.notCollected}
        label={t("brandBrain.counters.notCollected", "Not collected")}
        valueClassName="text-warning"
      />
    </dl>
  );
}

function Counter({ value, label, valueClassName }: { value: number; label: string; valueClassName?: string }) {
  return (
    <div className="flex flex-col">
      <dd className={cn("text-[22px] leading-[1.1] font-semibold tracking-[-0.01em] tabular-nums text-foreground", valueClassName)}>
        {value}
      </dd>
      <dt className={cn(MONO_LABEL, "mt-[3px]")}>{label}</dt>
    </div>
  );
}

function SectionStatus({ gapCount }: { gapCount: number }) {
  const { t } = useTranslation();
  if (gapCount === 0) {
    return <span className="ml-auto flex-none text-[10px] text-gray-400">{t("brandBrain.complete", "complete")}</span>;
  }
  return (
    <span className="ml-auto flex-none text-[10px] font-semibold tabular-nums text-warning">
      {t("brandBrain.gapCount", {
        count: gapCount,
        defaultValue_one: "{{count}} gap",
        defaultValue_other: "{{count}} gaps",
      })}
    </span>
  );
}

function RowLegend() {
  const { t } = useTranslation();
  return (
    <aside
      className="flex flex-wrap items-center gap-3.5 rounded-md border bg-card px-3.5 py-2.5"
      aria-labelledby="brand-brain-legend-heading"
    >
      <h2 id="brand-brain-legend-heading" className={MONO_LABEL}>
        {t("brandBrain.legend.title", "How to read a row")}
      </h2>
      <p className="inline-flex items-center gap-[7px]">
        <span className={NOT_COLLECTED_CHIP}>{t("brandBrain.legend.notCollectedLabel", "Not collected")}</span>
        <span className="text-[11.5px] text-muted-foreground">
          {t("brandBrain.legend.notCollectedHint", "a gap is information, not a blank")}
        </span>
      </p>
      <p className="inline-flex items-center gap-[7px]">
        <span className={cn(MONO_LABEL, "text-secondary-subtle-foreground")}>
          {t("brandBrain.legend.editedLabel", "Edited")}
        </span>
        <span className="text-[11.5px] text-muted-foreground">
          <Trans
            i18nKey="brandBrain.legend.editedHint"
            defaults="set only when someone changes a field <bold>here in Nexus</bold>. Blank means never edited here, not missing."
            components={{ bold: <b className="font-semibold text-secondary-subtle-foreground" /> }}
          />
        </span>
      </p>
    </aside>
  );
}
